import { useState, useRef, useCallback, useEffect, useMemo } from 'react';

import { TObjPromise, genPromise } from 'core/helpers/data';
import { useStableCallback } from 'core/hooks/useStableCallback';

import {
  TAbstractEntity,
  TAbstractNewEntity,
  TEditorConfig,
} from './Editor/interface';
import { TContextValue } from './context';

type TTask =
  | null
  | { mode: 'change'; entityUid: string }
  | { mode: 'create'; entityUid?: undefined };

export default function useOpenEditor<
  TContainer extends TAbstractEntity | null,
  TEntity extends TAbstractEntity,
  TNewEntity extends TAbstractNewEntity,
>(config: TEditorConfig<TContainer, TEntity, TNewEntity>) {
  const [container, setContainer] = useState<TContainer | null>(null);
  const [entity, setEntity] = useState<TNewEntity | TEntity | null>(null);
  const refPromise = useRef<TObjPromise<TEntity | null> | null>(null);
  const [task, setTask] = useState<TTask>(null);

  const editEntity = useStableCallback(
    async (entityUid: string): Promise<TEntity | null> => {
      setTask({ mode: 'change', entityUid });
      refPromise.current = genPromise<TEntity | null>();
      return await refPromise.current;
    }
  );

  const createEntity = useStableCallback(
    async (entityUid: string): Promise<TEntity | null> => {
      setTask({ mode: 'create' });
      setContainer(await config.apiGetContainer(entityUid));
      refPromise.current = genPromise<TEntity | null>();
      return await refPromise.current;
    }
  );

  useEffect(() => {
    if (!task) {
      setEntity(null);
      return;
    }

    if (task.mode === 'create') {
      setEntity(config.genNewEntity());
    } else {
      config
        .apiGetEntity(task.entityUid)
        .then(async entity => {
          const container =
            config.mode === 'withContainer'
              ? await config.apiGetContainer(config.getParentUid(entity))
              : null;
          setEntity(entity);
          setContainer(container);
        })
        .catch(orDie(refPromise.current).reject);
    }
  }, [task]);

  const onResult = useCallback((entity: TEntity | null): void => {
    setTask(null);
    setContainer(null);
    orDie(refPromise.current).resolve(entity);
  }, []);

  const ctxValue: TContextValue<TEntity> = useMemo(
    () => ({
      editEntity,
      createEntity,
    }),
    [editEntity, createEntity]
  );

  return {
    ctxValue,
    onResult,
    entity,
    container,
    mode: task?.mode ?? null,
  };
}
